<template>
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Formulario -->
      <ConfirmSendCorreoModal
        :visible="showModal"
        :destinatarios="form.destinatarios"
        @close="showModal = false"
        @confirm="send"
      />

      <form class="flex flex-col gap-4 w-full lg:w-1/2" @submit.prevent="showModal = true">
        <p class="text-neutral-500 text-sm font-light font-['Poppins']">
          Provisional mientras falla la notificación por WhatsApp (ActiveCampaign).
        </p>

        <div v-for="(r, i) in resultados" :key="i"
             class="px-4 py-2 rounded-md text-sm font-['Poppins']"
             :class="r.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
          <strong>{{ r.email }}</strong>: {{ r.msg }}
        </div>
        <div v-if="error" class="px-4 py-2 rounded-md text-sm bg-red-100 text-red-800 font-['Poppins']">
          {{ error }}
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-neutral-500 text-sm font-medium font-['Poppins']">Para (correo del destinatario)</label>
          <input v-model="form.destinatarios" type="text" required
                 class="px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins']"
                 placeholder="correo@ejemplo.com, otro@ejemplo.com" />
          <small class="text-neutral-400 text-xs">Puedes poner varios separados por coma.</small>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-neutral-500 text-sm font-medium font-['Poppins']">Asunto</label>
          <input v-model="form.asunto" type="text" required
                 class="px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins']" />
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-neutral-500 text-sm font-medium font-['Poppins']">Contenido</label>
          <textarea v-model="form.contenido" rows="18" required
                    class="px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins']"
                    placeholder="Pega aquí el mensaje que se enviaba por WhatsApp"></textarea>
          <small class="text-neutral-400 text-xs">
            Los links se vuelven clicables, el de <em>redirectContact</em> se muestra como botón y <em>*texto*</em> se pone en negritas.
          </small>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-neutral-500 text-sm font-medium font-['Poppins']">Enviar por</label>
          <select v-model="form.via"
                  class="px-4 py-[9px] bg-white rounded-md border border-[#dee2e6] text-neutral-500 text-sm font-light font-['Poppins']">
            <option value="brevo">Brevo (remitente mkt@tuin.ai)</option>
            <option value="smtp">SMTP del servidor (.env)</option>
          </select>
        </div>

        <div class="flex justify-end gap-2 sm:gap-4">
          <button type="button" class="custom-cancel-btn" :disabled="loading" @click="preview">
            <span class="custom-cancel-btn-text">Vista previa</span>
          </button>
          <button type="submit" class="custom-create-btn" :disabled="loading" :class="{ 'disabled-button': loading }">
            <span class="custom-create-btn-text">{{ loading ? 'Enviando...' : 'Enviar' }}</span>
          </button>
        </div>
      </form>

      <!-- Vista previa -->
      <div class="flex flex-col gap-1.5 w-full lg:w-1/2">
        <label class="text-neutral-500 text-sm font-medium font-['Poppins']">Vista previa</label>
        <iframe v-if="html" :srcdoc="html" class="w-full h-[650px] border border-[#dee2e6] rounded-md"></iframe>
        <p v-else class="text-neutral-400 text-sm font-['Poppins']">
          Presiona "Vista previa" para ver cómo llegará el correo.
        </p>
      </div>
    </div>
</template>

<script>
  import axios from "axios";
  import { ref, computed } from "vue";
  import { useStore } from "vuex";
  import ConfirmSendCorreoModal from "@/components/notificaciones/Modal/ConfirmSendCorreoModal.vue";

  export default {
    components: { ConfirmSendCorreoModal },
    setup() {
      const store = useStore();
      const session = computed(() => store.getters.getUserSession);
      const baseUrl = `${process.env.VUE_APP_API_BASE_URL}/admin/notificaciones/correo`;

      const form = ref({
        destinatarios: "",
        asunto: "Nuevo contacto",
        contenido: "",
        via: "brevo",
      });
      const html = ref("");
      const resultados = ref([]);
      const error = ref("");
      const loading = ref(false);
      const showModal = ref(false);

      const options = () => ({
        headers: { Authorization: `Bearer ${session.value.token_api}` }
      });

      const mensajeError = (e) => {
        // Cada login (Gabi o panel Laravel) genera un token nuevo e invalida el anterior
        if (e.response?.status === 401) return "Tu sesión ya no es válida (¿iniciaste sesión en otro lado?). Cierra sesión y vuelve a entrar.";
        if (e.response?.status === 403) return "Tu usuario no tiene rol Admin, no puede enviar notificaciones.";
        if (e.response?.status === 422) return Object.values(e.response.data.errors).flat().join(" ");
        return "Ocurrió un error al conectar con el servidor.";
      };

      const preview = async () => {
        error.value = "";
        if (!form.value.contenido.trim()) {
          error.value = "Escribe el contenido para ver la vista previa.";
          return;
        }
        loading.value = true;
        try {
          const response = await axios.post(`${baseUrl}/preview`, { contenido: form.value.contenido }, options());
          html.value = response.data.html;
        } catch (e) {
          console.error("Error en vista previa:", e);
          error.value = mensajeError(e);
        } finally {
          loading.value = false;
        }
      };

      const send = async () => {
        showModal.value = false;
        error.value = "";
        resultados.value = [];
        loading.value = true;
        try {
          const response = await axios.post(baseUrl, form.value, options());
          resultados.value = response.data.resultados;
          await preview();
        } catch (e) {
          console.error("Error al enviar el correo:", e);
          error.value = mensajeError(e);
        } finally {
          loading.value = false;
        }
      };

      return { form, html, resultados, error, loading, showModal, preview, send };
    }
  };
</script>
